import Notification from 'components/ui/base/notification/Notification';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getHttpStatus, sleep } from 'utils/common';
import { getErrorMessage } from 'utils/getErrorMessage';
import { uploadFile } from 'utils/uploadFile';

const extractUuidFromPresignUrl = (preSignUrl: string) => {
    const match = preSignUrl.match(/\/([^/?]+)\?/);
    return match?.[1] ?? null;
};

export type ProfilePictureStatus = 'idle' | 'fetching' | 'none' | 'ready' | 'verifying' | 'error';

type FetchPictureFetchState =
    | { state: 'ready'; url: string }
    | { state: 'none' }
    | { state: 'verifying' }
    | { state: 'error' };

type Args = {
    getPicture: () => Promise<string | null>;
    getPreSign: (file: File) => Promise<string>;
    uploadPicture: (preSignUrl: string) => Promise<any>;
    deletePicture: () => Promise<any>;
};

type Options = {
    pollIntervalMs?: number;
    pollTimeoutMs?: number;
};

export const useProfilePicture = (args: Args | (() => Args), options: Options = {}) => {
    const { deletePicture, getPicture, getPreSign, uploadPicture } = args instanceof Function ? args() : args;
    const { pollIntervalMs = 5000, pollTimeoutMs = 120000 } = options;

    const [profilePicture, setProfilePicture] = useState<string | null>(null);
    const [status, setStatus] = useState<ProfilePictureStatus>('idle');

    // Used to cancel polling if component unmounts or a new upload starts
    const pollAbortRef = useRef<{ aborted: boolean } | null>(null);

    // Used for fetching the image once (without polling) when the page loads
    const fetchOnce = useCallback(async (): Promise<FetchPictureFetchState> => {
        try {
            const url = await getPicture();

            if (url) {
                return { state: 'ready', url };
            }

            return { state: 'none' };
        } catch (e: any) {
            const httpStatus = getHttpStatus(e);

            // We get this error when we don't have the URL in the cms database
            if (httpStatus === 404) {
                return { state: 'none' };
            }

            // We get this error when we have the URL in the cms database but it is still verifying
            if (httpStatus === 500) {
                return { state: 'verifying' };
            }

            return { state: 'error' };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // It takes some time for the BE to verify the image and until then the url from getPicture will be undefined
    const pollUntilReady = useCallback(async () => {
        const controller = { aborted: false };
        pollAbortRef.current = controller;

        const started = Date.now();

        while (!controller.aborted && Date.now() - started < pollTimeoutMs) {
            const result = await fetchOnce();

            if (result.state === 'ready') {
                setProfilePicture(result.url);
                setStatus('ready');
                return;
            }

            if (result.state === 'none') {
                setProfilePicture(null);
                setStatus('none');
                return;
            }

            if (result.state === 'error') {
                setStatus('error');
                return;
            }

            // still verifying
            await sleep(pollIntervalMs);
        }

        if (!controller.aborted) {
            setStatus('error');
        }
    }, [fetchOnce, pollIntervalMs, pollTimeoutMs]);

    const abortPolling = useCallback(() => {
        if (pollAbortRef.current) {
            pollAbortRef.current.aborted = true;
        }
    }, []);

    const handleProfilePictureUpload = useCallback(
        async (file: File) => {
            abortPolling();
            setStatus('fetching');

            try {
                const preSignUrl = await getPreSign(file);
                await uploadFile(file, preSignUrl);

                Notification({
                    type: 'success',
                    messageTitle: 'Upload Successful',
                    description:
                        'Your file has been uploaded. We are verifying the image, which may take a few seconds. Please wait while we complete the process.',
                    placement: 'top',
                });

                const uuid = extractUuidFromPresignUrl(preSignUrl);
                if (!uuid) throw new Error('Invalid upload response');

                await uploadPicture(uuid);

                setStatus('verifying');
                await pollUntilReady();
            } catch (err: any) {
                const error = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Upload Failed',
                    description: error || 'Could not upload image. Please try again.',
                    placement: 'top',
                });
                setStatus('error');
                throw err;
            }
        },
        [abortPolling, getPreSign, uploadPicture, pollUntilReady]
    );

    const handleProfilePictureDelete = useCallback(async () => {
        abortPolling();

        setProfilePicture(null);
        setStatus('none');

        try {
            await deletePicture();

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Successfully removed image.',
                placement: 'top',
            });
        } catch (e: any) {
            const errorMessage = getErrorMessage(e);
            Notification({
                type: 'error',
                messageTitle: 'Upload Failed',
                description: errorMessage || 'Could not upload image. Please try again.',
                placement: 'top',
            });
            setStatus('error');
            throw e;
        }
    }, [abortPolling, deletePicture]);

    // Get the profile picture when the page loads
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setStatus('fetching');

            const result = await fetchOnce();
            if (cancelled) return;

            if (result.state === 'ready') {
                setProfilePicture(result.url);
                setStatus('ready');
            }

            if (result.state === 'none') {
                setProfilePicture(null);
                setStatus('none');
            }

            if (result.state === 'verifying') {
                setStatus('verifying');
                await pollUntilReady();
            }

            if (result.state === 'error') {
                setStatus('error');
            }
        };

        init();

        return () => {
            cancelled = true;
            abortPolling();
        };
    }, [fetchOnce, pollUntilReady, abortPolling]);

    return {
        profilePicture,
        status,
        isVerifying: status === 'verifying',
        isFetching: status === 'fetching',
        refetch: fetchOnce,
        handleProfilePictureUpload,
        handleProfilePictureDelete,
    };
};
